import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import IntegrationsCarousel from "./IntegrationsCarousel";
import { integrations } from "./landingData";

const TRUST = [
  { fr: "Essai gratuit 14 jours", en: "14-day free trial",     ar: "تجربة مجانية 14 يومًا" },
  { fr: "Sans carte de crédit",   en: "No credit card",         ar: "بدون بطاقة ائتمان"      },
  { fr: "Annulation à tout moment", en: "Cancel anytime",      ar: "إلغاء في أي وقت"        },
];

/* Minimal particle dots for ambient depth */
const PARTICLES = [
  { size: 2, x: "8%",  y: "18%", dur: "7s",  delay: "0s"    },
  { size: 1, x: "92%", y: "12%", dur: "9s",  delay: "1.2s"  },
  { size: 2, x: "3%",  y: "72%", dur: "11s", delay: "0.6s"  },
  { size: 1, x: "96%", y: "65%", dur: "8s",  delay: "2s"    },
  { size: 2, x: "50%", y: "5%",  dur: "10s", delay: "0.3s"  },
  { size: 1, x: "22%", y: "88%", dur: "6s",  delay: "1.8s"  },
  { size: 2, x: "78%", y: "82%", dur: "12s", delay: "0.9s"  },
];

const LandingHero = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const t = (obj) => obj[lang] || obj.fr;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <style>{`
        @keyframes mw-glow-breathe {
          0%, 100% { opacity: 0.85; }
          50%       { opacity: 1;    }
        }
        @keyframes mw-particle-drift {
          0%, 100% { transform: translate(0, 0);      opacity: 0.35; }
          33%       { transform: translate(6px, -10px); opacity: 0.6;  }
          66%       { transform: translate(-4px, 5px);  opacity: 0.25; }
        }
        @keyframes mw-pill-float-a {
          0%, 100% { transform: translateY(0px);   }
          50%       { transform: translateY(-10px); }
        }
        @keyframes mw-pill-float-b {
          0%, 100% { transform: translateY(-4px); }
          50%       { transform: translateY(8px);  }
        }
        .mw-glow-top    { animation: mw-glow-breathe 5s ease-in-out infinite; }
        .mw-glow-bottom { animation: mw-glow-breathe 7s ease-in-out infinite reverse; }
        .mw-pill-a      { animation: mw-pill-float-a 4s ease-in-out infinite; }
        .mw-pill-b      { animation: mw-pill-float-b 5.5s ease-in-out infinite; }
      `}</style>

      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" style={{ position: "relative", overflow: "hidden" }}>
        {/* Ambient glow orbs */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
          <div
            className="mw-glow-top"
            style={{
              position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)",
              width: "700px", height: "500px",
              background: "radial-gradient(ellipse, rgba(94,106,210,0.13) 0%, transparent 70%)",
              filter: "blur(60px)",
            }}
          />
          <div
            className="mw-glow-bottom"
            style={{
              position: "absolute", bottom: "5%", left: "50%", transform: "translateX(-50%)",
              width: "900px", height: "300px",
              background: "radial-gradient(ellipse, rgba(94,106,210,0.07) 0%, transparent 70%)",
              filter: "blur(80px)",
            }}
          />
          {/* Particle dots */}
          {PARTICLES.map((p, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: p.x, top: p.y,
                width: `${p.size}px`, height: `${p.size}px`,
                borderRadius: "50%",
                backgroundColor: "#5e6ad2",
                animation: `mw-particle-drift ${p.dur} ease-in-out infinite`,
                animationDelay: p.delay,
              }}
            />
          ))}
        </div>

        <div className="max-w-5xl mx-auto text-center" style={{ position: "relative" }}>
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{ marginBottom: "24px" }}
          >
            <span
              style={{
                display: "inline-block",
                fontSize: "12px", fontWeight: 500, letterSpacing: "0.4px",
                textTransform: "uppercase", padding: "4px 14px", borderRadius: "9999px",
                color: "#5e6ad2", backgroundColor: "rgba(94,106,210,0.10)",
                border: "1px solid rgba(94,106,210,0.20)",
              }}
            >
              {t({ fr: "Plateforme RH · EMEA · Multi-Pays", en: "HR Platform · EMEA · Multi-Country", ar: "منصة RH · EMEA · متعددة الدول" })}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            style={{
              color: "#f7f8f8",
              fontSize: "clamp(40px, 6vw, 64px)",
              fontWeight: 700,
              lineHeight: lang === "ar" ? 1.4 : 1.05,
              letterSpacing: "-2.5px",
              marginBottom: "20px",
              direction: lang === "ar" ? "rtl" : "ltr",
            }}
          >
            {t({
              fr: "La Plateforme RH Complète pour les Entreprises EMEA",
              en: "The Complete HR Platform for EMEA Companies",
              ar: "منصة الموارد البشرية الشاملة لشركات EMEA",
            })}
          </motion.h1>

          {/* Subhead */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.16 }}
            style={{
              color: "#d0d6e0",
              fontSize: "18px", fontWeight: 400, lineHeight: 1.55,
              letterSpacing: "-0.1px", maxWidth: "560px", margin: "0 auto 32px",
            }}
          >
            {t({
              fr: "Recrutement, paie, présence, congés et gestion des talents — conforme aux législations locales dans toute la région EMEA.",
              en: "Hiring, payroll, attendance, leave and talent management — compliant with local labour laws across the EMEA region.",
              ar: "التوظيف والرواتب والحضور والإجازات وإدارة المواهب — متوافق مع قوانين العمل المحلية في منطقة EMEA.",
            })}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.24 }}
            className="flex flex-col sm:flex-row gap-3 justify-center"
            style={{ marginBottom: "24px" }}
          >
            <button
              onClick={() => navigate("/register")}
              className="inline-flex items-center justify-center gap-2 transition-all"
              style={{
                backgroundColor: "#5e6ad2", color: "#ffffff",
                fontSize: "14px", fontWeight: 500, padding: "10px 20px",
                borderRadius: "8px", border: "none", cursor: "pointer",
                boxShadow: "0 0 20px rgba(94,106,210,0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#828fff";
                e.currentTarget.style.boxShadow = "0 0 32px rgba(130,143,255,0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#5e6ad2";
                e.currentTarget.style.boxShadow = "0 0 20px rgba(94,106,210,0.3)";
              }}
            >
              {t({ fr: "Essai Gratuit 14 jours", en: "Start Free 14-day Trial", ar: "ابدأ التجربة المجانية 14 يومًا" })}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollTo("features")}
              className="inline-flex items-center justify-center gap-2 transition-colors"
              style={{
                backgroundColor: "#0f1011", color: "#f7f8f8",
                fontSize: "14px", fontWeight: 500, padding: "10px 20px",
                borderRadius: "8px", border: "1px solid #23252a", cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#141516")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0f1011")}
            >
              {t({ fr: "Voir les fonctionnalités", en: "Explore features", ar: "استكشف الميزات" })}
            </button>
          </motion.div>

          {/* Trust row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.36 }}
            className="flex flex-wrap items-center justify-center gap-6"
          >
            {TRUST.map((item, i) => (
              <div key={i} className="flex items-center gap-1.5" style={{ color: "#62666d", fontSize: "12px" }}>
                <div style={{ width: "4px", height: "4px", borderRadius: "50%", backgroundColor: "#5e6ad2", flexShrink: 0 }} />
                {t(item)}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Dashboard mockup with floating data pills */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.44, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-screen-2xl mx-auto mt-14"
          style={{
            position: "relative",
            boxShadow: "0 0 0 1px #23252a, 0 32px 80px rgba(0,0,0,0.6)",
            borderRadius: "16px",
          }}
        >
          <img
            src="/assets/landing/dashboard.png"
            alt={t({ fr: "Tableau de bord Mawered", en: "Mawered dashboard", ar: "لوحة تحكم Mawered" })}
            loading="eager"
            width="1920"
            height="1080"
            style={{ display: "block", width: "100%", height: "auto", borderRadius: "16px" }}
          />

          {/* Floating pill A — left side (large screens only) */}
          <motion.div
            className="hidden lg:block"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ position: "absolute", left: "-28px", top: "32%" }}
          >
            <div
              className="mw-pill-a"
              style={{
                background: "rgba(15,16,17,0.92)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: "1px solid rgba(39,166,68,0.3)",
                borderRadius: "10px",
                padding: "10px 14px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.45)",
                minWidth: "160px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "4px" }}>
                <div style={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: "#27a644", boxShadow: "0 0 4px #27a644", flexShrink: 0 }} />
                <span style={{ color: "#27a644", fontSize: "9px", fontWeight: 600, letterSpacing: "0.4px" }}>PAIE · TUNISIE</span>
              </div>
              <div style={{ color: "#f7f8f8", fontSize: "11px", fontWeight: 500, lineHeight: 1.4 }}>
                {t({ fr: "3 fiches vérifiées ✓", en: "3 slips verified ✓", ar: "3 ملفات مراجعة ✓" })}
              </div>
              <div style={{ color: "#62666d", fontSize: "9px", marginTop: "2px" }}>
                {t({ fr: "Calculée automatiquement", en: "Auto-calculated", ar: "محسوب تلقائيًا" })}
              </div>
            </div>
          </motion.div>

          {/* Floating pill B — top area (large screens only) */}
          <motion.div
            className="hidden lg:block"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ position: "absolute", right: "12%", top: "-44px" }}
          >
            <div
              className="mw-pill-b"
              style={{
                background: "rgba(15,16,17,0.92)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: "1px solid rgba(94,106,210,0.3)",
                borderRadius: "10px",
                padding: "10px 14px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.45)",
                minWidth: "175px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "4px" }}>
                <div style={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: "#5e6ad2", boxShadow: "0 0 4px #5e6ad2", flexShrink: 0 }} />
                <span style={{ color: "#5e6ad2", fontSize: "9px", fontWeight: 600, letterSpacing: "0.4px" }}>ONBOARDING</span>
              </div>
              <div style={{ color: "#f7f8f8", fontSize: "11px", fontWeight: 500, lineHeight: 1.4 }}>
                {t({ fr: "S. Trabelsi · Jour 3/5", en: "S. Trabelsi · Day 3/5", ar: "س. طرابلسي · يوم 3/5" })}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "5px" }}>
                {[1,2,3,4,5].map((n) => (
                  <div key={n} style={{ height: "3px", flex: 1, borderRadius: "2px", backgroundColor: n <= 3 ? "#5e6ad2" : "#23252a" }} />
                ))}
              </div>
            </div>
          </motion.div>

          {/* AI Insight floating widget — bottom-right */}
          <motion.div
            initial={{ opacity: 0, x: 20, y: 10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 1.0, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: "absolute", bottom: "-20px", right: "-16px",
              background: "rgba(15,16,17,0.92)",
              backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
              border: "1px solid rgba(94,106,210,0.35)",
              borderRadius: "12px", padding: "12px 16px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(94,106,210,0.1)",
              width: "220px", zIndex: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
              <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#5e6ad2", boxShadow: "0 0 6px #5e6ad2", flexShrink: 0 }} />
              <span style={{ color: "#5e6ad2", fontSize: "10px", fontWeight: 600, letterSpacing: "0.5px" }}>AI INSIGHT</span>
            </div>
            <div style={{ color: "#f7f8f8", fontSize: "12px", lineHeight: 1.5, fontWeight: 500 }}>
              {t({ fr: "Anomalie paie détectée — 3 fiches à vérifier", en: "Payroll anomaly detected — 3 slips to review", ar: "شذوذ في الرواتب — 3 ملفات للمراجعة" })}
            </div>
            <div style={{ color: "#62666d", fontSize: "10px", marginTop: "4px" }}>
              {t({ fr: "Il y a 2 minutes", en: "2 minutes ago", ar: "منذ دقيقتين" })}
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Integrations strip */}
      <section
        className="py-10 px-4 overflow-hidden"
        style={{ backgroundColor: "#0f1011", borderTop: "1px solid #23252a", borderBottom: "1px solid #23252a" }}
      >
        <div className="max-w-7xl mx-auto">
          <p
            className="text-center uppercase tracking-widest mb-6"
            style={{ color: "#62666d", fontSize: "11px", fontWeight: 500, letterSpacing: "0.4px" }}
          >
            {t({
              fr: "Intégrations avec les outils leaders du marché",
              en: "Integrations with market-leading tools",
              ar: "التكامل مع الأدوات الرائدة في السوق",
            })}
          </p>
          <IntegrationsCarousel integrations={integrations} />
        </div>
      </section>
    </>
  );
};

export default LandingHero;
